import axios from 'axios';
import {
  PaginatedResponse,
  User,
  UserStats,
  UserFilters,
  FilterOptions,
  ProcessingSession,
  Audio,
  ProcessingFilters,
  AudioFilters,
  DashboardStats,
  UserAnalytics,
  ProcessingAnalytics,
  FinancialAnalytics,
  LLMAnalytics,
  Referral,
  ReferralAnalytics,
  ReferralFilters,
  RealtimeSessionsStats,
  HourlyDistributionStats,
  EngagementMetrics,
  ContentMetrics,
  FileMetrics,
  SourcesAnalytics,
  SubscriptionsAnalytics,
  RetentionAnalytics,
  PaymentRetentionAnalytics,
} from '../types/api';

// Helper function to decode JWT token
const decodeToken = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

// Helper function to check if token is expired
const isTokenExpired = (token: string): boolean => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return true;
  }
  // Add 5 second buffer to prevent race conditions
  return decoded.exp * 1000 < Date.now() + 5000;
};

// Flag to prevent multiple redirects
let isRedirecting = false;

// Helper function to logout and redirect
const logoutAndRedirect = () => {
  if (isRedirecting) return;
  isRedirecting = true;

  localStorage.removeItem('token');
  localStorage.removeItem('user');

  // Use setTimeout to avoid interrupting current request handling
  setTimeout(() => {
    window.location.href = '/login';
  }, 100);
};

// Create axios instance
const api = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token and check expiration
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');

    if (token) {
      // Check if token is expired BEFORE sending request
      if (isTokenExpired(token)) {
        console.warn('Token expired, redirecting to login');
        logoutAndRedirect();
        return Promise.reject(new Error('Token expired'));
      }

      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);

    // Handle 401 Unauthorized - token invalid or expired
    if (error.response?.status === 401) {
      console.warn('Received 401 Unauthorized, redirecting to login');
      logoutAndRedirect();
    }

    throw error;
  }
);

// Users API
export const usersApi = {
  getUsers: (filters: UserFilters = {}): Promise<PaginatedResponse<User>> =>
    api.get('/users/', { params: filters }).then(res => res.data),

  getUser: (userId: number): Promise<User> =>
    api.get(`/users/${userId}`).then(res => res.data),

  getUserByTelegramId: (telegramId: string): Promise<User> =>
    api.get(`/users/telegram/${telegramId}`).then(res => res.data),

  getUserStats: (): Promise<UserStats> =>
    api.get('/users/stats').then(res => res.data),

  getFilterOptions: () =>
    api.get('/users/filter-options').then(res => res.data),

  updateUser: (userId: number, userData: Partial<User>): Promise<User> =>
    api.put(`/users/${userId}`, userData).then(res => res.data),

  blockUser: (userId: number): Promise<{ message: string; user: User }> =>
    api.post(`/users/${userId}/block`).then(res => res.data),

  unblockUser: (userId: number): Promise<{ message: string; user: User }> =>
    api.post(`/users/${userId}/unblock`).then(res => res.data),
};

// Processing API
export const processingApi = {
  getProcessingSessions: (filters: ProcessingFilters = {}): Promise<PaginatedResponse<ProcessingSession>> =>
    api.get('/processing/sessions', { params: filters }).then(res => res.data),
  
  getProcessingSession: (sessionId: string): Promise<ProcessingSession> =>
    api.get(`/processing/sessions/${sessionId}`).then(res => res.data),
  
  getAudioRecords: (filters: AudioFilters = {}): Promise<PaginatedResponse<Audio>> =>
    api.get('/processing/audio', { params: filters }).then(res => res.data),
  
  getAudioRecord: (audioId: number): Promise<Audio> =>
    api.get(`/processing/audio/${audioId}`).then(res => res.data),
  
  getProcessingStats: () =>
    api.get('/processing/stats').then(res => res.data),
};

// Stats API
export const statsApi = {
  getDashboardStats: (): Promise<DashboardStats> =>
    api.get('/stats/dashboard').then(res => res.data),

  getUserAnalytics: (days: number = 30): Promise<UserAnalytics> =>
    api.get('/stats/users', { params: { days } }).then(res => res.data),

  getProcessingAnalytics: (days: number = 30, includeBlocked: boolean = false): Promise<ProcessingAnalytics> =>
    api.get('/stats/processing', { params: { days, include_blocked: includeBlocked } }).then(res => res.data),

  getFinancialAnalytics: (days: number = 30): Promise<FinancialAnalytics> =>
    api.get('/stats/financial', { params: { days } }).then(res => res.data),

  getLLMAnalytics: (days: number = 30): Promise<LLMAnalytics> =>
    api.get('/stats/llm', { params: { days } }).then(res => res.data),

  getRealtimeSessionsStats: (): Promise<RealtimeSessionsStats> =>
    api.get('/stats/sessions/realtime').then(res => res.data),

  getHourlyDistribution: (days: number = 7): Promise<HourlyDistributionStats> =>
    api.get('/stats/sessions/hourly-distribution', { params: { days } }).then(res => res.data),

  getEngagementMetrics: (days: number = 30, includeBlocked: boolean = false): Promise<EngagementMetrics> =>
    api.get('/stats/engagement', { params: { days, include_blocked: includeBlocked } }).then(res => res.data),

  getContentMetrics: (days: number | null = null, includeBlocked: boolean = false): Promise<ContentMetrics> =>
    api.get('/stats/content', { params: { days, include_blocked: includeBlocked } }).then(res => res.data),

  getFileMetrics: (days: number | null = null, includeBlocked: boolean = false): Promise<FileMetrics> =>
    api.get('/stats/files', { params: { days, include_blocked: includeBlocked } }).then(res => res.data),

  getSourcesAnalytics: (days: number | null = null, includeBlocked: boolean = false): Promise<SourcesAnalytics> =>
    api.get('/stats/sources', { params: { days, include_blocked: includeBlocked } }).then(res => res.data),

  getSubscriptionsAnalytics: (days: number = 30, includeBlocked: boolean = false): Promise<SubscriptionsAnalytics> =>
    api.get('/stats/subscriptions', { params: { days, include_blocked: includeBlocked } }).then(res => res.data),

  getRetentionAnalytics: (
    periodType: 'daily' | 'weekly' = 'weekly',
    cohortsCount: number = 26,
    retentionPeriods: number = 26,
    subscriptionType?: string,
    source?: string,
    includeBlocked: boolean = false
  ): Promise<RetentionAnalytics> =>
    api.get('/stats/retention', {
      params: {
        period_type: periodType,
        cohorts_count: cohortsCount,
        retention_periods: retentionPeriods,
        subscription_type: subscriptionType,
        source: source,
        include_blocked: includeBlocked,
      },
    }).then(res => res.data),

  getPaymentRetentionAnalytics: (
    subscriptionType: 'weekly' | 'monthly' = 'weekly',
    cohortsCount: number = 26,
    retentionPeriods: number = 26,
    retentionMode: 'strict' | 'flexible' = 'strict',
    source?: string,
    includeBlocked: boolean = false
  ): Promise<PaymentRetentionAnalytics> =>
    api.get('/stats/retention-payment', {
      params: {
        subscription_type: subscriptionType,
        cohorts_count: cohortsCount,
        retention_periods: retentionPeriods,
        retention_mode: retentionMode,
        source: source,
        include_blocked: includeBlocked,
      },
    }).then(res => res.data),
};

// Referrals API
export const referralsApi = {
  getReferrals: (filters: ReferralFilters = {}): Promise<PaginatedResponse<Referral>> =>
    api.get('/referrals/', { params: filters }).then(res => res.data),

  getReferral: (referralId: number): Promise<Referral> =>
    api.get(`/referrals/${referralId}`).then(res => res.data),

  getUserReferrals: (userId: number): Promise<Referral[]> =>
    api.get(`/referrals/user/${userId}/referred`).then(res => res.data),

  getReferralAnalytics: (days: number = 30): Promise<ReferralAnalytics> =>
    api.get('/stats/referrals', { params: { days } }).then(res => res.data),

  updateReferral: (referralId: number, data: Partial<Referral>): Promise<Referral> =>
    api.put(`/referrals/${referralId}`, data).then(res => res.data),
};

// Health check
export const healthApi = {
  check: (): Promise<{ status: string; message: string }> =>
    api.get('/../../health').then(res => res.data),
};